import { useState, useRef, useLayoutEffect } from "react";
import * as THREE from "three";

const buildings = [
  {
    id: 1,
    name: "Elbphilharmonie",
    src: "/images/building/Elbphilharmonie.png",
    hoverName: "META CARD",
    // Mediengruppe Hamburg
  },
  {
    id: 2,
    name: "Uploads2",
    src: "/images/building/uploads_files_6102055_building_5.png",
    hoverName: "Meta Global Capital USA",
  },
  {
    id: 3,
    name: "Uploads1",
    src: "/images/building/uploads_files_2437298_001.png",
    hoverName: "Godmode Media",
  },
  {
    id: 4,
    name: "Church",
    src: "/images/building/Churche_Frauenkirche_Munich.png",
    hoverName: "Mediengruppe München",
  },
  {
    id: 5,
    name: "BrandenburgGate",
    src: "/images/building/BrandenburgGate2.png",
    hoverName: "Mediengruppe Berlin",
  },
  {
    id: 6,
    name: "Mnogohome",
    src: "/images/building/Erlangennew.png",
    hoverName: "MGD Partner",
  },
  {
    id: 7,
    name: "Para",
    src: "/images/building/para.png",
    hoverName: "Mediengruppe Zypern",
  },
  {
    id: 8,
    name: "Building",
    src: "/images/building/sothebys_111w57_04.png",
    hoverName: "Meta-Welt der Mediengruppe",
  },
  {
    id: 9,
    name: "Sinwellturm",
    src: "/images/building/Sinwellturm.png",
    hoverName: "Mediengruppe Nürnberg",
  },
];

const ITEM_HEIGHT = 64; // Approx height of each item (md:w-16 md:h-16 = 64px)
const VISIBLE_COUNT = 4;
const BUFFER = Math.ceil(VISIBLE_COUNT / 2); // Increased buffer for smoother infinite scroll
const TOTAL_VISIBLE = VISIBLE_COUNT + BUFFER * 2;

export default function BuildingCarousel({
  showButtons,
  fadeInButtons,
  brandenburgRef,
  modelRefs,
  handleSelect,
}) {
  const [offset, setOffset] = useState(0);
  const [hovered, setHovered] = useState(null);
  const [cursor, setCursor] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  const totalItems = buildings.length;

  // Duplicate array multiple times for seamless loop
  // Create a larger buffer of items for smoother infinite scroll
  const multiplier = Math.ceil((VISIBLE_COUNT + BUFFER * 4) / buildings.length) + 4;
  const extendedBuildings = Array(multiplier).fill(buildings).flat();

  const [isTransitioning, setIsTransitioning] = useState(false);

  const scrollUp = () => {
    if (isTransitioning) return;
    
    setOffset((prev) => {
      const newOffset = prev - 1;
      if (newOffset < 0) {
        // Schedule a reset after animation
        setTimeout(() => {
          setIsTransitioning(true);
          setOffset(totalItems - 1);
          requestAnimationFrame(() => {
            setTimeout(() => setIsTransitioning(false), 50);
          });
        }, 300); // Match the transition duration
      }
      return newOffset;
    });
  };

  const scrollDown = () => {
    if (isTransitioning) return;

    setOffset((prev) => {
      const newOffset = prev + 1;
      if (newOffset >= totalItems) {
        // Schedule a reset after animation
        setTimeout(() => {
          setIsTransitioning(true);
          setOffset(0);
          requestAnimationFrame(() => {
            setTimeout(() => setIsTransitioning(false), 50);
          });
        }, 300); // Match the transition duration
      }
      return newOffset;
    });
  };

  return (
    <>
      <div
        className={`fixed top-1/2 right-2 sm:right-4 md:right-6 lg:right-8 transform -translate-y-1/2 flex flex-col items-center gap-2 sm:gap-3 md:gap-4 z-50 transition-opacity duration-[1000ms] ${
          fadeInButtons ? "opacity-100" : "opacity-0"
        }`}
      >
        {/* Up arrow */}
        <button
          className="w-10 h-10 sm:w-12 sm:h-12 bg-white text-black rounded-full 
          hover:bg-gray-200 flex items-center justify-center"
          onClick={scrollUp}
        >
          ↑
        </button>

        {/* Carousel container with smooth transform */}
        <div
          ref={containerRef}
          className="flex flex-col gap-2 sm:gap-3 md:gap-4 overflow-hidden"
          style={{ height: `${VISIBLE_COUNT * ITEM_HEIGHT}px` }}
        >
          <div
            className={`flex flex-col gap-2 sm:gap-3 md:gap-4 transition-transform duration-300 ${
              isTransitioning ? 'transition-none' : ''
            }`}
            style={{
              transform: `translateY(${-offset * ITEM_HEIGHT}px)`,
            }}
          >
            {extendedBuildings.map((item, index) => {
              const realIndex = (index - BUFFER + totalItems * 10) % totalItems;
              const building = buildings[realIndex];

              return (
                <div
                  key={`${item.id}-${index}`}
                  className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 border border-white rounded-md overflow-hidden cursor-pointer hover:scale-105 hover:border-sky-800 transition-transform duration-300"
                  onClick={() => {
                    const modelIndex = realIndex;
                    const ref = modelRefs.current[modelIndex];
                    if (!ref?.current) return;

                    const box = new THREE.Box3().setFromObject(ref.current);
                    const center = box.getCenter(new THREE.Vector3());
                    const size = box.getSize(new THREE.Vector3());
                    const radius = size.length() / 2;

                    handleSelect(center, radius, building.name);
                  }}
                  onMouseEnter={(e) => {
                    setHovered(building);
                    setCursor({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseMove={(e) => {
                    setCursor({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseLeave={() => setHovered(null)}
                >
                  <img
                    src={building.src}
                    alt={building.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Down arrow */}
        <button
          className="w-10 h-10 sm:w-12 sm:h-12 bg-white text-black rounded-full 
          hover:bg-gray-200 flex items-center justify-center"
          onClick={scrollDown}
        >
          ↓
        </button>
      </div>

      {/* Hover tooltip */}
      {hovered && (
        <Tooltip text={hovered.hoverName || hovered.name} cursor={cursor} />
      )}
    </>
  );
}

function Tooltip({ text, cursor }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!ref.current) return;

    const { offsetWidth: w, offsetHeight: h } = ref.current;
    const padding = 10;
    const offset = 20;

    let left = cursor.x + offset;
    let top = cursor.y + offset;

    if (left + w > window.innerWidth - padding) {
      left = cursor.x - w - offset;
    }
    if (top + h > window.innerHeight - padding) {
      top = cursor.y - h - offset;
    }

    setPos({ left, top });
  }, [cursor, text]);

  return (
    <div
      ref={ref}
      className="fixed pointer-events-none z-[9999] px-4 py-2 border border-white text-white rounded-md font-medium tracking-wide bg-black/80 transition"
      style={{
        top: pos.top,
        left: pos.left,
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </div>
  );
}